import axios, { AxiosInstance, InternalAxiosRequestConfig, AxiosError } from 'axios';
import { offlineCache, CACHE_KEYS } from './offlineCache';

// ✅ 3. Types TypeScript stricts (alignés backend)
export interface AuthUser { id: string; email: string; }
export interface AuthSession { access_token: string; refresh_token?: string; expires_in?: number; }
export interface ScanTicketRes { task_id: string; receipt_id: string; message: string; estimated_seconds: number; }
export interface ScanStatusRes { status: 'processing' | 'completed' | 'failed'; result?: any; error?: string; data?: { receipt?: any; duplicate_warning?: { message: string; existing_receipt_id: string; store_name: string; amount: number; }; [key: string]: any; }; }
export interface ScanProductRes { status: string; product: any; allergens: any; carcinogens: any; price_comparison: any; summary: any; }
export interface WalletRes { balance: number; currency: string; transactions: any[]; }
export interface ProfileRes { profile: any; allergies: any[]; family: any[]; subscription: any; [key: string]: any; }

const API_BASE_URL = (import.meta as any).env?.VITE_API_BASE_URL || '/api';

// ✅ Instance Axios de base
const api: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
  timeout: 30000
});

// Helper : Session ID anonymous (UUID persisté en localStorage)
function getAnonSessionId(): string {
  const key = 'receiptai_anon_session';
  let sid = localStorage.getItem(key);
  if (!sid || !/^[0-9a-f-]{36}$/.test(sid)) {
    sid = crypto.randomUUID();
    localStorage.setItem(key, sid);
  }
  return sid;
}

// 🔄 Intercepteur Requête : Injection token sécurisé + session anonymous
api.interceptors.request.use((config: InternalAxiosRequestConfig) => {
  const token = localStorage.getItem('receiptai_token');
  if (token && token !== 'guest') {
    config.headers.Authorization = `Bearer ${token}`;
  } else {
    // Utilisateur non connecté : envoyer le session ID anonymous
    config.headers['x-session-id'] = getAnonSessionId();
  }
  if (!(config.data instanceof FormData)) {
    config.headers['Content-Type'] = 'application/json';
  }
  return config;
});

// 🛡️ Intercepteur Réponse : Gestion erreurs + Refresh auto token
api.interceptors.response.use(
  (res) => res,
  async (error: AxiosError) => {
    const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean };

    // ✅ 4. Rafraîchissement automatique si 401
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;
      const refreshToken = localStorage.getItem('receiptai_refresh_token');

      if (refreshToken) {
        try {
          // Appel endpoint refresh (à créer côté backend si non présent)
          const { data } = await axios.post(`${API_BASE_URL}/auth/refresh`, { refresh_token: refreshToken });
          localStorage.setItem('receiptai_token', data.session.access_token);
          if (data.session.refresh_token) localStorage.setItem('receiptai_refresh_token', data.session.refresh_token);
          
          originalRequest.headers.Authorization = `Bearer ${data.session.access_token}`;
          return api(originalRequest); // Retry requête originale
        } catch (refreshErr) {
          // Refresh échoué → déconnexion propre
          clearSession();
        }
      } else {
        clearSession();
      }
    }
    return Promise.reject(error.response?.data || { error: 'Network error' });
  }
);

// Helper : Nettoyage session
const clearSession = () => {
  const token = localStorage.getItem('receiptai_token');
  // Ne pas rediriger si c'est un invité
  if (token === 'guest') return;
  localStorage.removeItem('receiptai_token');
  localStorage.removeItem('receiptai_refresh_token');
  localStorage.removeItem('receiptai_user');
  if (window.location.pathname !== '/onboarding') {
    window.location.href = '/onboarding';
  }
};

// ✅ 1 & 2. Services typés avec try/catch
export const apiService = {
  auth: {
    async register(email: string, password: string, meta: Record<string, any> = {}) {
      const { data } = await api.post<any>('/auth/register', { email, password, ...meta });
      const token = data.session?.access_token || data.token;
      const refresh = data.session?.refresh_token;
      if (token) localStorage.setItem('receiptai_token', token);
      if (refresh) localStorage.setItem('receiptai_refresh_token', refresh);
      if (data.user) localStorage.setItem('receiptai_user', JSON.stringify(data.user));
      return data;
    },
    async login(email: string, password: string) {
      const { data } = await api.post<any>('/auth/login', { email, password });
      const token = data.session?.access_token || data.token;
      const refresh = data.session?.refresh_token;
      if (token) localStorage.setItem('receiptai_token', token);
      if (refresh) localStorage.setItem('receiptai_refresh_token', refresh);
      if (data.user) localStorage.setItem('receiptai_user', JSON.stringify(data.user));
      return data;
    },
    async forgotPassword(email: string) {
      const { data } = await api.post('/auth/forgot-password', { email });
      return data;
    },
    async logout() {
      try { await api.post('/auth/logout'); } catch {}
      clearSession();
    },
    async getProfile() {
      const { data } = await api.get<{ user: any; profile: any }>('/auth/me');
      return data;
    }
  },

  scan: {
    async uploadTicket(imageFile: File) {
      const formData = new FormData();
      formData.append('image', imageFile);
      const { data } = await api.post<ScanTicketRes>('/scan/ticket', formData);
      return data;
    },
    async scanProduct(barcode: string, postalCode?: string, city?: string, latitude?: number, longitude?: number) {
      const body: Record<string, string | number> = { barcode };
      if (postalCode) body.postal_code = postalCode;
      if (city) body.city = city;
      if (typeof latitude === 'number') body.latitude = latitude;
      if (typeof longitude === 'number') body.longitude = longitude;
      const { data } = await api.post<ScanProductRes>('/scan/product', body);
      return data;
    },
    async getStatus(taskId: string) {
      const { data } = await api.get<ScanStatusRes>(`/scan/status/${taskId}`);
      return data;
    }
  },

  wallet: {
    async get() {
      try {
        const { data } = await api.get<WalletRes>('/wallet');
        offlineCache.set(CACHE_KEYS.WALLET, data, 6 * 60 * 60 * 1000); // 6h
        return data;
      } catch (err) {
        const cached = offlineCache.getStale<WalletRes>(CACHE_KEYS.WALLET);
        if (cached) return { ...cached, _fromCache: true };
        throw err;
      }
    },
    async getPendingCashbacks() {
      const { data } = await api.get('/wallet/cashback/pending');
      return data;
    },
    async requestWithdrawal(amount: number, method: 'bank' | 'paypal' | 'stripe', details: Record<string, any>) {
      const { data } = await api.post('/wallet/withdraw', { amount, method, details });
      return data;
    },
    async getTransactions(limit = 20, offset = 0) {
      const { data } = await api.get<{ transactions: any[] }>('/wallet', { params: { limit, offset } });
      return data;
    }
  },

  profile: {
    async get() {
      try {
        const { data } = await api.get<ProfileRes>('/profile');
        offlineCache.set(CACHE_KEYS.PROFILE, data);
        return data;
      } catch (err) {
        const cached = offlineCache.getStale<ProfileRes>(CACHE_KEYS.PROFILE);
        if (cached) return { ...cached, _fromCache: true };
        throw err;
      }
    },
    async update(updates: Record<string, any>) {
      const { data } = await api.put('/profile', updates);
      return data;
    },
    async syncAllergies(allergies: string[]) {
      const { data } = await api.put('/profile/allergies/sync', { allergens: allergies });
      return data;
    },
    async getFamily() {
      const { data } = await api.get('/profile/family');
      return data;
    },
    async addFamilyMember(member: { name: string; first_name?: string; last_name?: string; birth_year?: number; allergies?: string[] }) {
      const { data } = await api.post('/profile/family', member);
      return data;
    },
    async updateFamilyMember(id: string, updates: { name?: string; first_name?: string; last_name?: string; birth_year?: number; allergies?: string[] }) {
      const { data } = await api.put(`/profile/family/${id}`, updates);
      return data;
    },
    async deleteFamilyMember(id: string) {
      const { data } = await api.delete(`/profile/family/${id}`);
      return data;
    },
    async inviteFamilyMember(email: string) {
      const { data } = await api.post('/profile/family/invite', { email });
      return data;
    },
    async uploadAvatar(file: File) {
      const formData = new FormData();
      formData.append('avatar', file);
      const { data } = await api.post('/profile/avatar', formData);
      return data;
    },
    async deleteAccount() {
      const { data } = await api.delete('/profile');
      return data;
    },
    async exportData() {
      const { data } = await api.get('/profile/export-data');
      return data;
    }
  },

  alerts: {
    async getActive(types?: string[], limit = 20) {
      try {
        const params: Record<string, any> = { limit };
        if (types?.length) params.types = types.join(',');
        const { data } = await api.get('/alerts', { params });
        const result = Array.isArray(data) ? data : (data?.alerts ?? []);
        offlineCache.set(CACHE_KEYS.ALERTS, result, 2 * 60 * 60 * 1000); // 2h
        return result;
      } catch (err) {
        const cached = offlineCache.getStale<any[]>(CACHE_KEYS.ALERTS);
        if (cached) return cached;
        throw err;
      }
    },
    async resolve(alertId: string) {
      const { data } = await api.post(`/alerts/${alertId}/resolve`);
      return data;
    }
  },

  receipts: {
    async getReceiptDetail(receiptId: string) {
      const { data } = await api.get(`/scan/receipts/${receiptId}/detail`);
      return data;
    },
    async requestManualClaim(receiptId: string) {
      const { data } = await api.post(`/receipts/${receiptId}/claim-manual`);
      return data;
    }
  },

  subscriptions: {
    async createCheckout(plan: string, period = 'month') {
      const { data } = await api.post('/subscriptions/create-checkout-session', { plan, period });
      return data;
    },
    async getMine() {
      const { data } = await api.get('/subscriptions/my-subscription');
      return data;
    },
    async interacRequest(plan: string, period = 'month') {
      const { data } = await api.post('/subscriptions/interac-request', { plan, period });
      return data;
    },
    async interacStatus(id: string) {
      const { data } = await api.get(`/subscriptions/interac-status/${id}`);
      return data;
    },
    async paypalCheckout(plan: string, period = 'month') {
      const { data } = await api.post('/subscriptions/paypal-checkout', { plan, period });
      return data;
    },
    async paypalCapture(orderId: string) {
      const { data } = await api.post('/subscriptions/paypal-capture', { order_id: orderId });
      return data;
    },
    async wireRequest(plan: string, period = 'month') {
      const { data } = await api.post('/subscriptions/wire-request', { plan, period });
      return data;
    }
  },

  export: {
    async getWalletTransactions(params?: { start_date?: string; end_date?: string }) {
      const { data } = await api.get('/export/wallet', { params });
      return data;
    }
  },

  budget: {
    async getMonthly() {
      const { data } = await api.get('/budget/monthly');
      return data;
    },
    async updateSettings(monthly_budget_limit: number) {
      const { data } = await api.put('/budget/settings', { monthly_budget_limit });
      return data;
    }
  },

  ethicalPoints: {
    async claimPoints(claimData: {
      product_barcode: string;
      product_name: string;
      alternative_barcode?: string;
      alternative_name: string;
      location?: {
        city: string;
        province: string;
        country: string;
      };
    }) {
      const { data } = await api.post('/ethical-points/claim', claimData);
      return data;
    },

    async getDashboard() {
      const { data } = await api.get('/ethical-points/dashboard');
      return data;
    },

    async getHistory(page = 1, limit = 20, status = 'confirmed') {
      const { data } = await api.get('/ethical-points/history', {
        params: { page, limit, status }
      });
      return data;
    }
  },

  admin: {
    async getStats() {
      const { data } = await api.get('/admin/stats');
      return data;
    },
    async getMetrics() {
      const { data } = await api.get('/admin/metrics');
      return data;
    },
    async getRevenues(period?: string) {
      const { data } = await api.get('/admin/revenues', { params: { period } });
      return data;
    },
    async getInteracPayments() {
      const { data } = await api.get('/admin/interac-payments');
      return data;
    },
    async confirmInteracPayment(id: string) {
      const { data } = await api.post(`/admin/interac-payments/${id}/confirm`);
      return data;
    },
    async rejectInteracPayment(id: string) {
      const { data } = await api.post(`/admin/interac-payments/${id}/reject`);
      return data;
    },
    async listUsers(params?: { page?: number; limit?: number; search?: string }) {
      const { data } = await api.get('/admin/users', { params });
      return data;
    }
  },

  referral: {
    async getMe() {
      const { data } = await api.get('/referral/me');
      return data;
    },
    async applyCode(code: string) {
      const { data } = await api.post('/referral/apply', { code });
      return data;
    },
    async getConfig() {
      const { data } = await api.get('/referral/config');
      return data;
    },
    async adminUpdateConfig(updates: Record<string, any>) {
      const { data } = await api.patch('/referral/admin/config', updates);
      return data;
    }
  },

  notifications: {
    async getVapidKey(): Promise<{ publicKey: string }> {
      const { data } = await api.get('/notifications/vapid-public-key');
      return data;
    },
    async subscribe(subscription: any, platform = 'web') {
      const { data } = await api.post('/notifications/subscribe', { subscription, platform });
      return data;
    },
    async unsubscribe() {
      const { data } = await api.delete('/notifications/unsubscribe');
      return data;
    },
    async send(payload: { user_id?: string; title: string; body: string; url?: string }) {
      const { data } = await api.post('/notifications/send', payload);
      return data;
    }
  }
};